import { getSourceById } from './registry'
import type { Provider } from './providers/types'
import { githubWikiProvider } from './providers/githubWiki'

// Simple provider factory that maps a registry entry to a Provider implementation
export function getProvider(sourceId: string): Provider | undefined {
  const src = getSourceById(sourceId)
  if (!src) return undefined
  switch (src.type) {
    case 'github-wiki':
      return githubWikiProvider(src.owner, src.repo, src.id)
    case 'local':
      return localProvider(src.basePath, src.defaultPage)
    default:
      return undefined
  }
}

// Minimal local provider for static markdown served from this app's /public.
// Supports an optional _Sidebar.md next to the pages, and treats absolute (/…) links as internal.
function localProvider(basePath: string, defaultPage = 'getting-started'): Provider {
  const normalize = (input: string) => (input || '').replace(/^\/+|\/+$/g, '')
  return {
    async fetchDoc(slug: string) {
      const clean = normalize(slug || defaultPage)
      const url = `${basePath}/${clean}.md`
      const res = await fetch(url, { cache: 'no-cache' })
      if (!res.ok) throw new Error(`Document not found: ${clean}`)
      const md = await res.text()
      return { md }
    },
    async fetchSidebar() {
      const res = await fetch(`${basePath}/_Sidebar.md`, { cache: 'no-cache' })
      return res.ok ? await res.text() : null
    },
    rewriteLink(href: string) {
      if (/^(https?:)?\/\//i.test(href)) return { externalTo: href }
      if (href.startsWith('/')) return { internalTo: href }
      return {}
    },
    normalizeSlug: normalize,
    defaultPage,
  }
}
